<template>
    <div :class="['relative group', stretchToWidgetSize ? 'h-full' : '']">
        <redesign-base-card
            :with-border="false"
            :shadow="shadowColor"
            :class="stretchToWidgetSize ? 'h-full' : ''"
        >
            <template #content>
                <div
                    :class="[
                        'redesign-statistic-card border transition-colors',
                        stretchToWidgetSize ? 'w-full h-full' : '',
                        alignmentClass
                    ]"
                    :style="cardStyle"
                >
                    <div
                        class="font-medium"
                        :style="labelStyle"
                        :title="resolvedLabel"
                    >
                        {{ resolvedLabel }}
                    </div>
                    <div
                        class="font-bold flex items-baseline gap-2"
                        :style="valueStyle"
                    >
                        <span
                            v-if="showAbsoluteCountValue"
                            class="text-xl"
                        >
                            ({{ absoluteValue }})
                        </span>

                        <span class="resolved-value">
                            {{ resolvedValue }}
                        </span>
                    </div>
                </div>
            </template>
        </redesign-base-card>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { COUNTERS_TYPE, PERCENTAGE_TYPE, TIME_TYPE } from '@/enum/redesignQueueDashboardStatistics'
import RedesignBaseCard from '@/components/RedesignBaseComponents/RedesignBaseCard.vue'
import { QueueDashboardCardConfig } from '@/types/dashboard'
import { errorSoundList, getSoundObject, warningSoundList } from '@/composables/globalData/useEntitiesData'
import { limitThresholdValue, warningThresholdValue } from '@/composables/useSoundsThresholdNotification'
import { setupNotification } from '@/composables/useQueueDashboardNotification'
import { removeSoundsFromQueueByIdWithHistoryKeep, updateSoundCurrentValue } from '@/composables/useSoundsStoreData'

const silentSoundRec_Path = '/sound/silence.mp3'

const props = withDefaults(
    defineProps<{
        label: string
        value: string | number
        absoluteValue: number
        type: typeof PERCENTAGE_TYPE | typeof COUNTERS_TYPE | typeof TIME_TYPE
        cardConfig: QueueDashboardCardConfig

        color: string
        labelFontSize?: string | number
        valueFontSize?: string | number
        showAbsoluteValue?: boolean
        stretchToWidgetSize?: boolean
        textAlign?: string
    }>(),
    {
        labelFontSize: 18,
        valueFontSize: 26,
        showAbsoluteValue: false,
        stretchToWidgetSize: false,
        textAlign: 'center'
    }
)

//Data
const notificationStatus = ref('')
const shadowColor = ref('')

//Computed
const ALIGNMENT_CLASS_MAP = {
    start: 'items-start text-left',
    center: 'items-center text-center',
    end: 'items-end text-right'
}

const alignmentClass = computed(() => {
    return ALIGNMENT_CLASS_MAP[props.textAlign] ?? ALIGNMENT_CLASS_MAP.center
})

const resolvedLabel = computed(() => {
    return props.cardConfig?.customLabel || props.label
})

const resolvedValue = computed(() => {
    if (props.type === PERCENTAGE_TYPE) {
        return `${props.value} %`
    }
    return props.value
})

const warningThreshold = computed(() => +(props.cardConfig?.notificationConfiguration?.warningThreshold ?? 0))
const limitThreshold = computed(() => + ( props.cardConfig?.notificationConfiguration?.limitThreshold ?? 0))
const warningColor = computed(() => props.cardConfig?.notificationConfiguration?.warningColor)
const limitColor = computed(() => props.cardConfig?.notificationConfiguration?.limitColor)
const warningSound = computed(() => {
    if (props.cardConfig?.notificationConfiguration.warningSound) {
        return getSoundObject(warningSoundList.value, props.cardConfig?.notificationConfiguration.warningSound)?.Rec_Path || silentSoundRec_Path
    }
    return silentSoundRec_Path
})
const limitSound = computed(() => {
    if (props.cardConfig?.notificationConfiguration.limitSound) {
        return getSoundObject(errorSoundList.value, props.cardConfig?.notificationConfiguration.limitSound)?.Rec_Path || silentSoundRec_Path
    }
    return silentSoundRec_Path
})

const resolvedColor = computed(() => {
    return shadowColor.value || props.cardConfig?.customColor || props.color
})

const cardStyle = computed(() => ({
    color: resolvedColor.value,
    borderColor: resolvedColor.value
}))

const labelStyle = computed(() => {
    const size = props.labelFontSize
    // Handle both number and numeric string values
    if (size === undefined || size === null) return {}
    const numericValue = typeof size === 'number' ? size : parseFloat(size)
    return {
        fontSize: !isNaN(numericValue) ? `${numericValue}px` : size
    }
})

const valueStyle = computed(() => {
    const size = props.valueFontSize
    // Handle both number and numeric string values
    if (size === undefined || size === null) return {}
    const numericValue = typeof size === 'number' ? size : parseFloat(size)
    return {
        fontSize: !isNaN(numericValue) ? `${numericValue}px` : size
    }
})

const showAbsoluteCountValue = computed(() => {
    if (props.type === TIME_TYPE || props.type === COUNTERS_TYPE) {
        return false
    }
    return props.showAbsoluteValue
})
const numericValue = computed<number>(() => {
    if (props.type === PERCENTAGE_TYPE) {
        return typeof props.value === 'string'
            ? parseFloat(props.value)
            : Number(props.value)
    }

    if (props.type === TIME_TYPE) {
        return props.absoluteValue
    }

    return Number(props.absoluteValue ?? props.value ?? 0)
})

const reverseThreshold = computed(() =>
    props.cardConfig?.notificationConfiguration?.reverseThreshold ?? false
)

const computedStatus = computed(() => {
    const val = numericValue.value

    if (!val) return ''

    if (reverseThreshold.value) {
        if (limitThreshold.value && val <= limitThreshold.value) {
            return limitThresholdValue
        }
        if (warningThreshold.value && val <= warningThreshold.value) {
            return warningThresholdValue
        }
    } else {
        if (limitThreshold.value && val >= limitThreshold.value) {
            return limitThresholdValue
        }
        if (warningThreshold.value && val >= warningThreshold.value) {
            return warningThresholdValue
        }
    }

    return ''
})

const previousStatus = ref('')

watch(
    computedStatus,
    (currentStatus) => {
        if (!currentStatus) {
            notificationStatus.value = ''
            shadowColor.value = ''
            previousStatus.value = ''
            removeSoundsFromQueueByIdWithHistoryKeep(`${resolvedLabel.value}-${props.type}`)
            return
        }

        const isLimit = currentStatus === limitThresholdValue
        const thresholdValue = isLimit
            ? limitThreshold.value
            : warningThreshold.value
        if(currentStatus && currentStatus === previousStatus.value) {
            updateSoundCurrentValue(
                'id',
                `The ${currentStatus} of  ${resolvedLabel.value} is ${thresholdValue}. <br> Current value is ${numericValue.value}.`
            )
            return
        }

        notificationStatus.value = currentStatus

        shadowColor.value = isLimit
            ? limitColor.value
            : warningColor.value

        if(currentStatus && !shadowColor.value) {
            shadowColor.value = resolvedColor.value
        }

        setupNotification({
            id: `${resolvedLabel.value}-${props.type}`,
            parameterName: resolvedLabel.value,
            currentValue: numericValue.value,
            thresholdValue,
            currentStatus,
            type: props.type,

            sound: isLimit
                ? limitSound.value
                : warningSound.value,

            soundPlayed: previousStatus.value === currentStatus
        })

        previousStatus.value = currentStatus
    },
    { immediate: true }
)


</script>

<style lang="scss">
.redesign-statistic-card {
  @apply px-4 py-3 flex flex-col justify-center gap-1 shadow-sm;
  background-color: var(--mainBgColor-color);
  border-radius: 8px;
  width: 340px;

  &.w-full {
    width: 100%;
  }

  &.h-full {
    height: 100%;
  }
  .resolved-value {
    direction: ltr;
  }
}
</style>

